import { FC, useCallback, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  RadixTable,
  SingleRadio,
  TColumn,
} from '@features/ui';
import {
  PayoutLogicFormData,
  schemaPayoutLogic,
  TPayoutLogicTable,
} from '@src/types/exchangePackages.ts';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { PayoutLogicDialog } from '@features/admin/packages/PayoutLogicDialog.tsx';

interface PayoutLogicProps {
  values?: PayoutLogicFormData;
  onSubmit?: (values: PayoutLogicFormData) => void;
}
export const PayoutLogic: FC<PayoutLogicProps> = ({ values, onSubmit }) => {
  const [dataTable, setDataTable] = useState<TPayoutLogicTable[]>([]);
  const [selectedData, setSelectedData] = useState<TPayoutLogicTable | null>(
    null
  );
  const [open, setOpen] = useState(false);
  const [isAdd, setIsAdd] = useState(false);

  const handleSubmitForm = (data: PayoutLogicFormData) => {
    onSubmit?.(data);
  };

  const handleChangePayoutLogicTable = useCallback(
    (value: TPayoutLogicTable) => {
      if (!value.id) {
        setDataTable((prev) =>
          prev.concat({
            ...value,
            id: `${prev.length + 1}`,
            payoutLogicNumber: `${prev.length + 1}`,
          })
        );

        return;
      }
      setDataTable((prev) =>
        prev.map((dt) => {
          if (dt.id === value.id) {
            return { ...value, ...dt };
          }

          return dt;
        })
      );
    },
    []
  );

  const { control, handleSubmit } = useForm<PayoutLogicFormData>({
    resolver: yupResolver(schemaPayoutLogic),
    defaultValues: values,
  });

  const handleOpen = useCallback((add: boolean) => {
    setIsAdd(add);
    setOpen(true);
  }, []);

  const handleEdit = useCallback(
    (item: TPayoutLogicTable) => {
      setSelectedData(item);
      handleOpen(false);
    },
    [handleOpen]
  );
  const handleAdd = useCallback(() => {
    setSelectedData(null);
    handleOpen(false);
  }, [handleOpen]);

  const columns = useMemo(
    (): TColumn<TPayoutLogicTable>[] => [
      {
        title: '#',
        className: 'w-10',
        render: (item) => <p>{item?.payoutLogicNumber}</p>,
      },
      {
        title: `Payout from nth`,
        render: (item) => <p>{item?.payoutFromNth ?? 0}</p>,
      },
      {
        title: `Payout to nth`,
        render: (item) => <p>{item?.payoutToNth ?? 0}</p>,
      },
      {
        title: `Trading Days Required`,
        render: (item) => <p>{item?.tradingDaysRequired ?? 0}</p>,
      },
      {
        title: `Daily Profit Required`,
        render: (item) => <p>${item?.dailyProfitRequired ?? 0}</p>,
      },
      {
        title: `Min Payout $`,
        render: (item) => <p>${item?.minPayout ?? 0}</p>,
      },
      {
        title: `Max Payout $`,
        render: (item) => <p>${item?.maxPayout ?? 0}</p>,
      },
      {
        title: `Required Balance Threshold $`,
        render: (item) => <p>${item?.requiredBalanceThreshold ?? 0}</p>,
      },
      {
        title: `Client Split % (after initial 100%)`,
        render: (item) => <p>{item?.clientSplit ?? 0}%</p>,
      },
      {
        title: (
          <div className={'w-full flex justify-end'}>
            <Button size={'sm'} variant={'lime'} onClick={handleAdd}>
              Add
            </Button>
          </div>
        ),
        render: (item) => (
          <div className={'flex gap-x-4 flex justify-end'}>
            <Button size={'sm'}>Delete</Button>
            <Button
              size={'sm'}
              variant={'lime'}
              onClick={() => handleEdit(item as TPayoutLogicTable)}
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [handleAdd, handleEdit]
  );

  return (
    <Card className={'text-xs min-h-170'}>
      <form autoComplete="off" onSubmit={handleSubmit(handleSubmitForm)}>
        <div className={'flex flex-col gap-y-2'}>
          <p className={'font-bold text-sm'}>PA Account Payout Information</p>
          <p>
            Riles / limitations for payouts on this account when traded as a PA.
          </p>
        </div>
        <div className={'flex flex-col gap-y-5 mt-5 mb-2'}>
          <Controller
            control={control}
            name="applyFirst"
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                id={`checkbox-apply`}
                label={'Apply first $12,500 rule to Payouts?'}
                onCheckedChange={(checked) => field.onChange(Boolean(checked))}
              />
            )}
          />
          <Controller
            control={control}
            name="allowMonthly"
            render={({ field }) => (
              <SingleRadio
                checked={field.value}
                label={'Allow Payouts Monthly (last Sat)'}
                onClick={() => field.onChange(!field.value)}
              />
            )}
          />
          <Controller
            control={control}
            name="allowMonthly"
            render={({ field }) => (
              <SingleRadio
                checked={field.value}
                label={'Allow Payouts Weekly (Sat)'}
                onClick={() => field.onChange(!field.value)}
              />
            )}
          />
        </div>
        <p className={'mb-11.5'}>
          {`Payouts subject to 'min trading days' rules. Addon(s) can override
          payout frequency above.`}
        </p>
        <RadixTable columns={columns} data={dataTable} />
      </form>
      <PayoutLogicDialog
        isAdd={isAdd}
        open={open}
        values={selectedData}
        onOpenChange={setOpen}
        onSubmit={handleChangePayoutLogicTable}
      />
    </Card>
  );
};
